import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * The one button in the application.
 *
 * Variants exist so that a screen expresses *importance*, not appearance. If a
 * new visual treatment is wanted, it belongs here as a named variant rather
 * than as class names sprinkled at a call site. That rule is what stops an
 * interface from accumulating eleven slightly different buttons.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'glass' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-text-on-accent shadow-2 hover:bg-accent-hover active:bg-accent-press border border-transparent',
  secondary:
    'bg-surface-2 text-text-1 border border-edge hover:bg-surface-3 hover:border-edge-strong',
  ghost: 'bg-transparent text-text-2 border border-transparent hover:bg-surface-2 hover:text-text-1',
  glass: 'glass text-text-1 hover:bg-glass-strong',
  danger: 'bg-danger text-text-on-accent border border-transparent hover:brightness-110',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[0.8125rem] gap-1.5 rounded-[var(--radius-sm)]',
  md: 'h-10 px-4 text-sm gap-2 rounded-[var(--radius-md)]',
  lg: 'h-12 px-6 text-[0.9375rem] gap-2.5 rounded-[var(--radius-lg)]',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Rendered before the label. Decorative, so it is hidden from assistive tech. */
  readonly icon?: ReactNode;
  readonly iconAfter?: ReactNode;
  /** Stretch to the width of the container. */
  readonly block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconAfter, block, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex select-none items-center justify-center font-medium',
        // Transform and opacity only: both are composited on the GPU, so a
        // press never triggers layout or paint on the main thread.
        'transition-[transform,background-color,border-color,opacity,filter]',
        'duration-[calc(var(--duration-fast)*var(--motion-scale))]',
        'active:scale-[0.97]',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {icon !== undefined && (
        <span aria-hidden="true" className="shrink-0">
          {icon}
        </span>
      )}
      {children}
      {iconAfter !== undefined && (
        <span aria-hidden="true" className="shrink-0">
          {iconAfter}
        </span>
      )}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'iconAfter' | 'block'> {
  /**
   * Required, not optional.
   *
   * An icon-only control is invisible to a screen reader without it, and
   * making the label mandatory in the type is more reliable than a lint rule
   * or a code review catching it.
   */
  readonly label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'ghost', size = 'md', label, className, children, ...rest },
  ref,
) {
  const square = size === 'sm' ? 'size-8' : size === 'lg' ? 'size-12' : 'size-10';
  return (
    <button
      ref={ref}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        'transition-[transform,background-color,border-color,opacity]',
        'duration-[calc(var(--duration-fast)*var(--motion-scale))]',
        'active:scale-[0.94]',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANT_CLASSES[variant],
        square,
        size === 'sm' ? 'rounded-[var(--radius-sm)]' : 'rounded-[var(--radius-md)]',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});
